"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useRef, useState } from "react";
import makeStyles from "../../theming/make-styles.js";
import Attachment from "../attachment/index.js";
import useCombinedRefs from "../utils/use-combined-refs.js";
export const useStyles = makeStyles()((theme, { variant, color, children }) => {
    const palette = theme.palette(color).tooltip[variant];
    return {
        content: {
            padding: !children ? theme.spacing("xs", "sm") : undefined,
        },
        wrapper: {
            zIndex: 1020,
            display: "inline-flex",
            alignItems: "center",
            fontSize: theme.typography.small.fontSize,
            color: palette.color,
            backgroundColor: palette.backgroundColor,
            border: `${theme.size(1)} solid`,
            borderColor: palette.borderColor,
            borderRadius: theme.size(4),
        },
    };
});
export const Tooltip = React.forwardRef(function Tooltip(_a, ref) {
    var { children, className, color = "primary", delayIn = 0, hoverable = false, delayOut = hoverable ? 100 : 0, openRef, strategy = "align-center", triggerElement, variant = "dark", onClose, onOpen } = _a, props = __rest(_a, ["children", "className", "color", "delayIn", "hoverable", "delayOut", "openRef", "strategy", "triggerElement", "variant", "onClose", "onOpen"]);
    const [open, setOpen] = useState(false);
    const attachmentRef = useRef(null);
    const { classes, cx } = useStyles({ color, variant, children: !!children && typeof children === "object" && "key" in children });
    const innerRef = useRef(null);
    const combinedRef = useCombinedRefs(ref, innerRef);
    const handleClose = useCallback(() => {
        if (open) {
            setOpen(false);
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }
    }, [open, onClose]);
    const handleOpen = useCallback(() => {
        if (!open) {
            setOpen(true);
            onOpen === null || onOpen === void 0 ? void 0 : onOpen();
        }
    }, [open, onOpen]);
    const handleBlur = useCallback((e) => {
        var _a, _b;
        if ("onBlur" in triggerElement.props) {
            (_b = (_a = triggerElement.props).onBlur) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        }
        handleClose();
    }, [handleClose, triggerElement.props]);
    const handleFocus = useCallback((e) => {
        var _a, _b;
        if ("onFocus" in triggerElement.props) {
            (_b = (_a = triggerElement.props).onFocus) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        }
        handleOpen();
    }, [handleOpen, triggerElement.props]);
    const handleMouseLeave = useCallback((e) => {
        setTimeout(() => {
            var _a, _b, _c, _d;
            if ("onMouseLeave" in triggerElement.props) {
                (_b = (_a = triggerElement.props).onMouseLeave) === null || _b === void 0 ? void 0 : _b.call(_a, e);
            }
            const open = !!((_c = combinedRef.current) === null || _c === void 0 ? void 0 : _c.matches(":hover")) || !!((_d = attachmentRef.current) === null || _d === void 0 ? void 0 : _d.matches(":hover"));
            if (!hoverable || !open) {
                handleClose();
            }
        }, delayOut);
    }, [combinedRef, delayOut, handleClose, hoverable, triggerElement.props]);
    const handleMouseEnter = useCallback((e) => {
        var _a, _b;
        if ("onMouseEnter" in triggerElement.props) {
            (_b = (_a = triggerElement.props).onMouseEnter) === null || _b === void 0 ? void 0 : _b.call(_a, e);
        }
        setTimeout(handleOpen, delayIn);
    }, [delayIn, handleOpen, triggerElement.props]);
    const handleToggleOpen = useCallback((open) => {
        if (open) {
            handleOpen();
        }
        else {
            handleClose();
        }
    }, [handleClose, handleOpen]);
    if (openRef) {
        openRef.current = { open, setOpen: handleToggleOpen };
    }
    return (_jsxs(_Fragment, { children: [React.cloneElement(triggerElement, {
                ref: combinedRef,
                onBlur: handleBlur,
                onFocus: handleFocus,
                onMouseEnter: handleMouseEnter,
                onMouseLeave: handleMouseLeave,
            }), children && open && (_jsx(Attachment, Object.assign({}, props, { ref: attachmentRef, anchorRef: innerRef, className: cx(classes.wrapper, className), "data-component": "tooltip", scrollLock: false, strategy: strategy, onClose: handleClose, onMouseLeave: handleMouseLeave, children: _jsx("div", { className: classes.content, children: children }) })))] }));
});
export default Tooltip;
//# sourceMappingURL=tooltip.js.map